import { SORT_DIRECTION_ASC } from '@disco/constants/SortTypes'
import { getContactStatus } from '@disco/utils/contacts'
import { getPartiallySelectedTags } from '@disco/utils/tags'
import intersection from 'lodash/intersection'
import { createSelector } from 'reselect'
import type { Contact, ContactId, RecipientList } from 'types/list'
import type { State } from 'types/redux'
import type { FullWriterSplit } from 'types/track'
import type { Label, Pro, Publisher, Rightsholder } from 'types/writer'
import type { State as ContactsState } from '@disco/state-management/reducers/entities/contacts'
import type { BusinessesCheckboxState } from '@disco/state-management/reducers/forms/list'
import {
  getCampaign,
  getCampaignRules,
} from '@disco/state-management/selectors/campaigns'
import { getEditedTrackWriterSplits } from '@disco/state-management/selectors/editor'
import { getEmailGroups } from '@disco/state-management/selectors/emailGroups'
import { getTrackWriterSplits } from '@disco/state-management/selectors/tracks'
const EMPTY_ARRAY: any[] = []
/**
 * Gets all pros (professionals) from the state.
 * @param state - The Redux state
 * @returns Array of pro objects
 */
export const getPros = (state: State): Pro[] => state.entities.pro
/**
 * Gets all publishers from the state.
 * @param state - The Redux state
 * @returns Array of publisher objects
 */
export const getPublishers = (state: State): Publisher[] =>
  state.entities.publishers

export const getRightsholderPro = (state: State): Pro[] =>
  state.entities.rightsholderPro
export const getRightsholderPublishers = (state: State): Publisher[] =>
  state.entities.rightsholderPublishers

export const getLabels = (state: State): Label[] => state.entities.labels

export const getRightsholders = (state: State): Rightsholder[] =>
  state.entities.rightsholders

/**
 * Transforms database entities to dropdown option format.
 * @param list - Array of database entities with id and name properties
 * @returns Array of dropdown options with label and value properties
 */
const transformDbToDropdownOptions = list =>
  list.map(v => ({
    label: v.name,
    value: v.id,
  }))

/**
 * Gets pros formatted for dropdown options.
 * @param state - The Redux state
 * @returns Array of dropdown option objects for pros
 */
export const getProDropdownOptions = createSelector(
  getRightsholderPro,
  pros => {
    return transformDbToDropdownOptions(pros)
  },
)
/**
 * Gets publishers formatted for dropdown options.
 * @param state - The Redux state
 * @returns Array of dropdown option objects for publishers
 */
export const getPublisherDropdownOptions = createSelector(
  getRightsholderPublishers,
  publishers => {
    return transformDbToDropdownOptions(publishers)
  },
)
/**
 * Gets the contacts state object.
 * @param state - The Redux state
 * @returns The contacts state containing contacts and metadata
 */
export const getContacts = (state: State): ContactsState =>
  state.entities.contacts
/**
 * Gets the contacts as an array.
 * @param state - The Redux state
 * @returns Array of contact objects
 */
export const getContactsArray = (state: State): Contact[] =>
  getContacts(state).contacts ?? []
/**
 * Gets the contact IDs array.
 * @param state - The Redux state
 * @returns Array of contact IDs
 */
export const getContactsIds = (state: State): ContactId[] =>
  getContacts(state).ids ?? []
/**
 * Checks if all contacts have been loaded.
 * @param state - The Redux state
 * @returns True if contacts are fully loaded, false otherwise
 */
export const isContactsLoaded: (state: State) => boolean = createSelector(
  [getContacts],
  contactsState => {
    const loaded =
      contactsState.loaded && !contactsState.loading && !contactsState.next
    return !!loaded
  },
)
/**
 * Gets contacts by their IDs, ensuring order is preserved.
 * @param state - The Redux state
 * @returns Array of contacts in the order of their IDs
 */
export const getContactsByIds = createSelector(
  [getContactsArray, getContactsIds],
  (contactsState, ids) => {
    const contacts = ids.map(
      id =>
        contactsState.find(contact => contact.id === id) || {
          id: id,
          loaded: false,
        },
    )
    return contacts
  },
)
/**
 * Gets contacts that are currently in edit mode.
 * @param state - The Redux state
 * @returns Array of contacts in edit mode
 */
const getEditedContacts: (state: State) => Contact[] = createSelector(
  getContactsArray,
  contacts => contacts.filter(c => c.editMode),
)
/**
 * Gets partially selected tags from edited contacts.
 * @param state - The Redux state
 * @returns Array of partially selected tags
 */
export const getPartiallySelectedTagsFromState = createSelector(
  getEditedContacts,
  contacts => getPartiallySelectedTags(contacts),
)
/**
 * Determines if batch mode is active (multiple contacts selected for editing).
 * @param state - The Redux state
 * @returns True if batch mode is active, false otherwise
 */
export const getBatchMode = createSelector(
  getEditedContacts,
  contacts => contacts.length > 1,
)
/**
 * Gets the saving status of contacts.
 * @param state - The Redux state
 * @returns True if contacts are currently being saved, false otherwise
 */
export const getContactsSavingStatus = (state: State): boolean =>
  getContacts(state).saving ?? false

/**
 * Gets the current sort parameter for contacts.
 * @param state - The Redux state
 * @returns The sort parameter string
 */
const getContactsSortParam = (state: State) => getContacts(state).sortParam

/**
 * Gets the current sort direction for contacts.
 * @param state - The Redux state
 * @returns The sort direction string
 */
const getContactsSortDirection = (state: State) =>
  getContacts(state).sortDirection

type GetContactListProps = {
  list?: RecipientList | null | undefined
  appendingContacts?: boolean | null | undefined
}
/**
 * Gets contacts list based on optional filtering criteria.
 * Can return all contacts, contacts in a specific list, or contacts to append to a list.
 * @param state - The Redux state
 * @param props - Optional props for filtering
 * @returns Array of filtered contacts
 */
export const getContactsList = createSelector(
  [
    getContacts,
    (_s, props?: GetContactListProps) => props && props.list,
    (_s, props?: GetContactListProps) => props && props.appendingContacts,
  ],
  (contactsState, list, appendingContacts) => {
    const allContacts = contactsState.contacts ?? []

    if (list) {
      const destListRecipients: number[] = []
      list.recipients.forEach(r => {
        if (r.contact) destListRecipients.push(r.contact.id)
      })

      if (appendingContacts) {
        // mode: add existing - append contacts to a list
        return allContacts
          .filter(
            c =>
              !(
                destListRecipients.includes(c.id) ||
                list.recipients.find(r => r.email === c.email)
              ),
          )
          .filter(c => c.email)
      } else {
        // mode: contacts in mailing list
        return allContacts.filter(
          c =>
            destListRecipients.includes(c.id) ||
            list.recipients.find(r => r.email === c.email),
        )
      }
    } else {
      // mode: all contacts
      return allContacts
    }
  },
)
/**
 * Gets contacts formatted for dropdown options, excluding deleted contacts.
 * @param state - The Redux state
 * @returns Array of dropdown option objects for contacts
 */
export const getContactsDropdownOptions = createSelector(
  getContactsList,
  contacts =>
    contacts
      .filter(c => !c.deleted)
      .map(c => ({
        label: c.name || c.email,
        value: c.id.toString(),
      })),
)

/**
 * Gets the current search term for contacts.
 * @param state - The Redux state
 * @returns The search term string
 */
const getSearchTerm = (state: State) => getContacts(state).searchTerm

/**
 * Factory function that creates a selector to get filtered contact list.
 * @returns A selector function for filtering contacts by inclusion/exclusion lists
 */
const makeGetFilteredContactList = () =>
  createSelector(
    [
      getContactsList,
      (_state, props) => JSON.stringify(props?.exclusionList || []),
      (_state, props) => JSON.stringify(props?.inclusionList || []),
    ],
    (list, _exclusionList, _inclusionList) => {
      const exclusionList = JSON.parse(_exclusionList)
      const inclusionList = JSON.parse(_inclusionList)
      return list.filter(
        c =>
          !exclusionList.includes(c.id) &&
          (!c.deleted || inclusionList.includes(c.id)),
      )
    },
  )

const writerTypes = ['Writer', 'Artist']
/**
 * Factory function that creates a selector to get writers and artists from contacts.
 * @returns A selector function for filtering contacts who are writers or artists
 */
export const makeGetWritersAndArtists = () =>
  createSelector([makeGetFilteredContactList()], contacts => {
    return contacts.filter(
      c => intersection(writerTypes, c.tags || []).length > 0,
    )
  })

/**
 * Gets writer names by their IDs.
 * @param state - The Redux state
 * @param props - Object containing writer IDs
 * @returns Array of writer names
 */
const getWriterNames = (state: State, { ids }: any): string[] => {
  if (!ids) return EMPTY_ARRAY
  const list = getContactsList(state)
  return list.filter(c => ids.includes(c.id)).map(w => w.name)
}

/**
 * Factory function that creates a selector to get writer names.
 * @returns A selector function for getting writer names by IDs
 */
export const makeGetWriterNames = (): typeof getWriterNames => getWriterNames

/**
 * Sorts and filters contacts based on parameters and search term.
 * @param contacts - Array of contact objects
 * @param list - The recipient list object
 * @param sortParam - The parameter to sort by
 * @param sortDirection - The sort direction (ASC/DESC)
 * @param searchTerm - The search term to filter by
 * @returns Sorted and filtered array of contacts
 */
const sortedContacts = (
  contacts: Contact[],
  list,
  sortParam,
  sortDirection,
  searchTerm: string,
) => {
  const lessThan = sortDirection === SORT_DIRECTION_ASC ? -1 : 1
  const moreThan = sortDirection === SORT_DIRECTION_ASC ? 1 : -1

  const getContactAttr = contact => {
    if (sortParam === 'Status') {
      return getContactStatus(contact, list && list.id)
    }

    const contactParamValue = contact[sortParam.toLowerCase()]
    return (
      Array.isArray(contactParamValue)
        ? contactParamValue.join(', ')
        : contactParamValue || ''
    ).trim()
  }

  const searchTerms = searchTerm
    ? searchTerm.toLowerCase().trim().replace(/\s+/gi, ' ').split(' ')
    : []
  const filteredContacts =
    searchTerms.length > 0
      ? contacts.slice().filter(c => {
          if (!c) return false
          let customDataString = ''

          if (c.custom_data && Object.keys(c.custom_data).length > 0) {
            customDataString = Object.values(c.custom_data)
              .filter(v => typeof v === 'string' && v.trim().length > 0)
              .join(' ')
          }

          const doc = `${c.name} ${c.email} ${c.phone} ${c.company} ${
            c.territory
          } ${customDataString} ${(c.tags || []).join(', ')}`.toLowerCase()
          return !searchTerms.some(term => !doc.includes(term))
        })
      : contacts.slice()
  return filteredContacts.sort((a, b) => {
    const aParam = getContactAttr(a)
    const bParam = getContactAttr(b)
    if (!aParam && bParam) return 1
    if (aParam && !bParam) return -1
    if (aParam.toLowerCase() < bParam.toLowerCase()) return lessThan
    if (aParam.toLowerCase() > bParam.toLowerCase()) return moreThan
    return 0
  })
}

/**
 * Gets filtered and sorted contacts excluding deleted ones.
 * @param state - The Redux state
 * @param props - Optional props for filtering
 * @returns Array of filtered, sorted, and visible contacts
 */
export const getFilteredContacts = createSelector(
  [
    getContactsList,
    (_s, props?: GetContactListProps) => props && props.list,
    getContactsSortParam,
    getContactsSortDirection,
    getSearchTerm,
  ],
  (contacts, list, sortParam, sortDirection, searchTerm) => {
    const visibleContacts = contacts.filter(c => !c.deleted)
    return sortedContacts(
      visibleContacts,
      list,
      sortParam,
      sortDirection,
      searchTerm ?? '',
    )
  },
)
/**
 * Gets contacts filtered by campaign recipient rules.
 * Filters contacts based on mailing list subscriptions, tags, and other criteria.
 * @param state - The Redux state
 * @returns Object containing filtered contacts and associated mailing lists
 */
export const getContactsFilteredByCampaignRules = createSelector(
  [getFilteredContacts, getCampaignRules, getEmailGroups],
  (contacts, campaignRules, emailGroups) => {
    const associatedLists = {}
    const subscribedContacts = contacts.filter(c => {
      return !(c.unsubscribes && c.unsubscribes.global)
    })
    const filteredContacts = subscribedContacts.filter(c => {
      for (const rule of campaignRules.rules) {
        let res = true
        let lvalue
        let rvalue = rule.ruleValue
        let op

        if (rule.ruleType === 'mailingList') {
          // handle mailingList rule separately as it's wildly differs
          // from the the rest of the rule types
          const mailingGroupId = parseInt(rule.ruleValue)
          const targetEmailGroup = emailGroups.find(
            g => g.id === mailingGroupId,
          )

          if (targetEmailGroup) {
            const contactBelongsToGroup = targetEmailGroup.recipients.find(
              r => r.contact && (r.contact.id === c.id || r.email === c.email),
            )
            const contactUnsubscribedFromGroup =
              c.unsubscribes &&
              c.unsubscribes.saved_recipients_ids.includes(targetEmailGroup.id)
            res = !!(contactBelongsToGroup && !contactUnsubscribedFromGroup)
          } else {
            res = false
          }

          if (res) associatedLists[c.id] = mailingGroupId
        } else if (
          // for backwards compatibility handle type rule as tags rule
          (rule.ruleType === 'tags' || rule.ruleType === 'type') &&
          rvalue
        ) {
          lvalue = c['tags']

          res = lvalue.includes(rvalue)
          res = rule.ruleOp === 'equals' ? res : !res
          if (res && campaignRules.matchMode === 'any') return true
          if (!res && campaignRules.matchMode === 'all') return false
        } else if (rvalue) {
          lvalue = (c[rule.ruleType] || '').toLowerCase().trim()
          rvalue = rvalue.toLowerCase().trim()
          op = rule.ruleOp

          switch (op) {
            case 'contains':
              res = lvalue.includes(`${rvalue}`)
              break

            case 'notContains':
              res = !lvalue.includes(`${rvalue}`)
              break

            case 'equals':
              res = lvalue === rvalue
              break

            case 'notEquals':
              res = lvalue !== rvalue
              break
          }
        }

        if (res && campaignRules.matchMode === 'any') return true
        if (!res && campaignRules.matchMode === 'all') return false
      }

      if (campaignRules.matchMode === 'any') {
        // normally false, except if there was zero rules
        return campaignRules.rules.length === 0
      }

      if (campaignRules.matchMode === 'all') return true
    })
    return {
      filteredContacts,
      associatedLists,
    }
  },
)
/**
 * Converts writer splits to full form with complete contact, pro, and publisher data.
 * @param splits - Array of writer split objects
 * @param contacts - Array of contact objects
 * @param pros - Array of pro objects
 * @param publishers - Array of publisher objects
 * @returns Array of full writer splits with complete data
 * @todo Fix object types
 */
export const convertSplitsToFullForm = (
  splits: Record<string, any>[],
  contacts: Record<string, any>[],
  pros: Record<string, any>[],
  publishers: Record<string, any>[],
): FullWriterSplit[] => {
  if (!splits) return EMPTY_ARRAY
  // TODO: remove workaround,
  // skip splits that refer to deleted writer as i can't show actual writer
  // in UI
  // @ts-expect-error - fix later
  return splits
    .map(s => ({
      ...s,
      pro: pros.find(p => p.id === s.pro),
      publisher: publishers.find(p => p.id === s.publisher),
      writer: contacts.find(c => c.id === s.writer),
    }))
    .filter(s => s.writer)
}
/**
 * Gets full writer splits with complete contact, pro, and publisher data.
 * @param state - The Redux state
 * @returns Array of full writer splits
 */
export const getFullWriterSplits = createSelector(
  [getTrackWriterSplits, getContactsList, getPros, getPublishers],
  (splits, contacts, pros, publishers) =>
    convertSplitsToFullForm(splits, contacts, pros, publishers),
)
/**
 * Gets full writer splits for the currently edited track.
 * @param state - The Redux state
 * @returns Array of full writer splits for edited track
 */
export const getEditedTrackFullWriterSplits = createSelector(
  [getEditedTrackWriterSplits, getContactsList, getPros, getPublishers],
  (splits, contacts, pros, publishers) =>
    convertSplitsToFullForm(splits, contacts, pros, publishers),
)
/**
 * Gets writer IDs for the currently edited track.
 * @param state - The Redux state
 * @returns Array of writer IDs for edited track
 */
export const getEditedTrackWriterIds = createSelector(
  getEditedTrackFullWriterSplits,
  splits => splits.map(s => s.writer.id),
)
/**
 * Gets a specific contact by ID with optional list filtering.
 * @param state - The Redux state
 * @param props - Object containing contact ID and optional list parameters
 * @returns The contact object or null if not found
 */
export const getContact = (
  state: State,
  {
    contactId,
    list,
    appendingContacts,
  }: {
    contactId: number
    list?: RecipientList | null | undefined
    appendingContacts?: boolean | null | undefined
  },
): Contact | null | undefined => {
  return getContactsList(state, {
    list,
    appendingContacts,
  }).find(c => c.id === contactId)
}
/**
 * Factory function that creates a selector to get a specific contact.
 * @returns A selector function for getting a contact by ID
 */
export const makeGetContact = (): typeof getContact => getContact
/**
 * Gets contacts that are currently selected.
 * @param state - The Redux state
 * @returns Array of selected contacts
 */
export const getSelectedContacts = createSelector(getContactsList, list =>
  list.filter(c => c.selected),
)
/**
 * Gets contact IDs from the list form.
 * @param state - The Redux state
 * @returns Array of contact IDs from the form
 */
export const getListFormContacts = (state: State): ContactId[] =>
  state.forms.listForm.recipients
/**
 * Gets IDs of contacts that were last imported.
 * @param state - The Redux state
 * @returns Array of imported contact IDs
 */
export const getImportedContactIds = (state: State): ContactId[] =>
  state.forms.listForm.lastImportContactIds
/**
 * Gets the recipient businesses checkbox state.
 * @param state - The Redux state
 * @returns The businesses checkbox state object
 */
export const getRecipientBusinesses = (state: State): BusinessesCheckboxState =>
  state.forms.listForm.recipientBusinesses
/**
 * Gets contacts that are selected in the form.
 * @param state - The Redux state
 * @returns Array of form contacts
 */
export const getFormContacts = createSelector(
  getContactsList,
  getListFormContacts,
  (list, formContacts) => {
    return list.filter(c => formContacts.includes(c.id))
  },
)
/**
 * Gets form contacts with customized business information.
 * @param state - The Redux state
 * @returns Array of form contacts with business customization
 * @todo Clean getBusinessCustomizedFormContacts after removal of LIST_MODAL_3_BUSINESSES
 */
export const getBusinessCustomizedFormContacts = createSelector(
  getFormContacts,
  getRecipientBusinesses,
  (formContacts, recipientBusinesses) =>
    formContacts.map(c => {
      if (!c.disco_user) return c
      const businessCheckboxes = recipientBusinesses[c.id]
      if (!businessCheckboxes) return c
      const business_ids = Object.keys(businessCheckboxes).map(n => parseInt(n))
      // selects all businesses even if they weren't since there is no form
      return { ...c, disco_user: { ...c.disco_user, business_ids } }
    }), // getFormContacts,
  // getRecipientBusinesses,
  // (formContacts, recipientBusinesses) =>
  //   formContacts.map(c => {
  //     if (!c.disco_user) return c
  //     const businessCheckboxes = recipientBusinesses[c.id]
  //     if (!businessCheckboxes) return c
  //     const business_ids = Object.keys(businessCheckboxes)
  //       .map(n => parseInt(n))
  //       .filter(b => businessCheckboxes[b] === 'all')
  //     return {
  //       ...c,
  //       disco_user: {
  //         ...c.disco_user,
  //         business_ids,
  //       },
  //     }
  //   }),
)
/**
 * Gets contacts that were imported in the last import operation.
 * @param state - The Redux state
 * @returns Array of imported contacts
 */
export const getImportedContacts = createSelector(
  getContactsList,
  getImportedContactIds,
  (list, importedContacts) => list.filter(c => importedContacts.includes(c.id)),
)
/**
 * Gets contact businesses with their checkbox states.
 * @param state - The Redux state
 * @returns Array of contact business objects with checkbox states
 */
export const getContactBusinesses = createSelector(
  getContactsList,
  getRecipientBusinesses,
  (contacts, recipientBusinesses) => {
    return Object.keys(recipientBusinesses)
      .map(recipientIdKey => {
        const recipientId = parseInt(recipientIdKey)
        const formBusinesses = recipientBusinesses[recipientId]
        const contact = contacts.find(c => c.id === recipientId)
        if (!contact || !contact.disco_user) return null
        return {
          id: recipientId,
          name: contact.name || contact.email,
          businesses: contact.disco_user.businesses.map(b => ({
            ...b,
            checked: formBusinesses?.[b.id],
          })),
        }
      })
      .filter(Boolean)
  },
)
/**
 * Gets the first contact from a campaign's recipients list.
 * @param state - The Redux state
 * @returns The first contact from the campaign, or null if no recipients
 */
export const getFirstContactFromCampaign = createSelector(
  [getCampaign, getContacts],
  (campaign, contactsState) => {
    if (campaign.recipients_list.length === 0) return null
    const randomContact = (contactsState.contacts ?? []).find(
      c => c.id === campaign.recipients_list[0]?.id,
    )
    return randomContact
  },
)
/**
 * Gets the contact that has a warning state.
 * @param state - The Redux state
 * @returns The contact with warning state, or null if none
 */
export const getWarningContact: (state: State) => Contact | null | undefined =
  createSelector([getContacts, getContactsArray], (contactsState, contacts) => {
    const warning = contactsState.warning
    if (warning == null) return undefined
    return contacts.find(c => c.id === warning)
  })
